/**
 * Faixa de filtro de período do modal fitness: chips 1/5/20/30 dias + navegação de data final.
 */
import { alpha, Box, Button, Stack, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";

import {
  FITNESS_RANGE_PRESETS,
  type FitnessRangeDays,
  isValidFitnessRangeDays,
  WearableDateRangeChips,
} from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { WearablePeriodDateRow } from "@/pages/medicalcare/components/Wearable/WearablePeriodDateRow";
import { formatWearablePeriodLabel } from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";

export interface FitnessPeriodHeaderProps {
  periodStart: Dayjs;
  periodEnd: Dayjs;
  rangeDays: FitnessRangeDays;
  today: Dayjs;
  loading: boolean;
  onRangeDaysChange: (days: FitnessRangeDays) => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onPickDate: (date: Dayjs) => void;
  onGoToToday?: () => void;
  compact?: boolean;
}

export function FitnessPeriodHeader({
  periodStart,
  periodEnd,
  rangeDays,
  today,
  loading,
  onRangeDaysChange,
  onPrevDay,
  onNextDay,
  onPickDate,
  onGoToToday,
  compact = false,
}: FitnessPeriodHeaderProps) {
  const periodLabel = formatWearablePeriodLabel(periodStart, periodEnd, rangeDays);
  const isToday = periodEnd.isSame(today, "day");

  return (
    <Box
      sx={{
        width: "100%",
        // Os próprios controles já criam a hierarquia visual; uma segunda
        // moldura deixaria o header pesado e com informação demais.
        px: 0,
        py: { xs: 0, md: 0.25 },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) auto" },
          gridTemplateAreas: {
            xs: '"title" "chips" "divider" "date"',
            md: '"date chips"',
          },
          alignItems: "center",
          columnGap: { md: 3 },
          rowGap: compact ? 0.5 : 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            gridArea: "title",
            display: { xs: "block", md: "none" },
            color: (theme) => alpha(theme.palette.common.white, 0.78),
            fontWeight: 700,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
          }}
        >
          Período dos dados
        </Typography>
        <Box
          sx={{
            gridArea: "chips",
            minWidth: 0,
            // Mantém leitura confortável sem competir com a data selecionada.
            "& .MuiButton-root": {
              minHeight: { sm: 36, md: 38 },
              px: { sm: 1.25, md: 1.4 },
              fontSize: { sm: "0.8rem", md: "0.825rem" },
            },
          }}
        >
          <WearableDateRangeChips
            rangeDays={rangeDays}
            onChange={(days) => {
              if (isValidFitnessRangeDays(days)) onRangeDaysChange(days);
            }}
            disabled={loading}
            tone="onPrimary"
            presets={FITNESS_RANGE_PRESETS}
          />
        </Box>

        <Box
          sx={{
            gridArea: "divider",
            display: { xs: "block", md: "none" },
            height: 1,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.16),
          }}
        />

        <Stack
          direction="row"
          spacing={{ xs: 0.75, sm: 1 }}
          alignItems="center"
          justifyContent={{ xs: "center", md: "flex-start" }}
          sx={{ gridArea: "date", minWidth: 0 }}
        >
          <WearablePeriodDateRow
            periodLabel={periodLabel}
            periodEnd={periodEnd}
            today={today}
            loading={loading}
            onPrevDay={onPrevDay}
            onNextDay={onNextDay}
            onPickDate={onPickDate}
          />
          {onGoToToday && (
            <Button
              size="small"
              variant="outlined"
              disabled={loading || isToday}
              onClick={onGoToToday}
              sx={{
                minWidth: { xs: 54, sm: 60 },
                minHeight: { sm: 38 },
                px: { xs: 1, sm: 1.25 },
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 700,
                fontSize: { xs: "0.8125rem", sm: "0.8125rem" },
                color: "white",
                borderColor: (theme) => alpha(theme.palette.common.white, 0.4),
                bgcolor: (theme) => alpha(theme.palette.common.white, 0.08),
                "&:hover": {
                  borderColor: (theme) => alpha(theme.palette.common.white, 0.65),
                  bgcolor: (theme) => alpha(theme.palette.common.white, 0.16),
                },
                "&.Mui-disabled": {
                  color: (theme) => alpha(theme.palette.common.white, 0.32),
                  borderColor: (theme) => alpha(theme.palette.common.white, 0.14),
                },
              }}
            >
              Hoje
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
